import { Component, TemplateFunction } from '@ribajs/core'

import type { ToastType } from '../types'

interface Scope {
  // OPTIONS
  type: ToastType
  message: string
  timeout: number
  // METHODS
  remove: SbToastComponent['remove']
  // TEMPLATES
}

const DEFAULT_CLASSES = [
  'p-4',
  'rounded-md',
  'shadow-md',
  'mb-4',
  'flex',
  'items-center',
  'justify-between',
  'space-x-4',
  'max-w-sm',
]

const TYPE_CLASSES: { [key in ToastType]: string[] } = {
  info: ['bg-blue', 'text-white'],
  success: ['bg-green-500', 'text-white'],
  warning: ['bg-yellow-500', 'text-black'],
  danger: ['bg-melone', 'text-white'],
}

/**
 * A toast component for the sb-toaster component
 *
 * # Attributes
 * - `type` [string] The type of the toast, can be `info`, `success`, `warning` or `danger`
 * - `message` [string] The message of the toast
 * - `timeout` [number] The timeout in milliseconds after the toast will be removed. If `0` the toast will not be removed automatically. Default: `5000`
 */
export class SbToastComponent extends Component {
  public static tagName = 'sb-toast'

  timer: ReturnType<typeof setTimeout> | null = null

  public scope: Scope = {
    // OPTIONS
    type: 'info',
    message: '',
    timeout: 5000,
    // METHODS
    remove: this.remove.bind(this),
    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return ['type', 'message', 'timeout']
  }

  /**
   * Create a new toast component
   */
  static create(
    message: string,
    type: ToastType,
    scope: Record<string, any> = {},
  ) {
    const toastCo = window.document.createElement(
      this.tagName,
    ) as SbToastComponent

    toastCo.setAttribute('type', type)
    toastCo.setAttribute('message', message)
    if (Object.keys(scope).length > 0) {
      toastCo.scope = {
        ...toastCo.scope,
        ...scope,
      }
    }
    return toastCo
  }

  protected requiredAttributes(): string[] {
    return ['type', 'message']
  }

  constructor() {
    super()
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue?: ToastType,
    newValue?: ToastType,
    namespace: string | null = null,
  ) {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    const oldClass = oldValue ? TYPE_CLASSES[oldValue] : []
    const newClass = newValue ? TYPE_CLASSES[newValue] : []
    switch (attributeName) {
      case 'type':
        this.classList.remove(...oldClass)
        this.classList.add(...newClass)
        break
    }
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbToastComponent.observedAttributes)
    this.classList.add(...DEFAULT_CLASSES)
    this.addListeners()
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeListeners()
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbToastComponent.tagName}-ready`)
  }

  protected addListeners() {
    if (this.scope.timeout > 0) {
      this.timer = setTimeout(() => {
        this.remove()
      }, this.scope.timeout)
    }
  }

  protected removeListeners() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return (
      <>
        <p rv-template="message"></p>
        <button type="button" rv-on-click="remove">
          <sb-icon class="block" src="icon-close.svg" size={16}></sb-icon>
        </button>
      </>
    )
  }
}
